import { useSelector } from 'react-redux';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { RootState } from '../../store';
import PrivateRoute from '../../PrivateRoutes';
import LicenseGatedRoute from './LicenseGatedRoute';
import PrivateLayout from '../../pages/PrivateLayout';
import ClientDetails from '../Clients/ClientDetails';
import ClientsList from '../Clients/ClientsList';
import CalendarViewPage from '../../pages/CalendarViewPage';
import LawyerDetails from '../Lawyers/LawyerDetails';
import Lawyers from '../Lawyers/Lawyers';
import FilesPage from '../../pages/FilesPage';
import DraftingPage from '../../pages/DraftingPage';
import ResearchPage from '../../pages/ResearchPage';
import OperationPage from '../../pages/OperationsPage';
import TaskDetail from '../Tasks/TaskDetail';
import TaskDeadlineTracker from '../Tasks/TaskDeadlineTracker';
import EmployeeDetail from '../Management/Employees/EmployeeDetail';
import TeamDetail from '../Management/Teams/TeamDetail';
import EmployeeList from '../Management/Employees/EmployeeList';
import FinancePage from '../../pages/FinancePage';
import ReportsPage from '../../pages/ReportsPage';
import TimeTrackingPage from '../../pages/TimeTrackingPage';
import MyTimeEntriesPage from '../../pages/MyTimeEntriesPage';
import FirmEconomicsDashboardPage from '../../pages/FirmEconomicsDashboardPage';
import TimeKeepingPage from '../../pages/TimeKeepingPage';
import TimeKeepingLogs from '../TimeTracker/TimeKeepingLogs';
import MessageCenterPage from '../../pages/MessageCenterPage';
import HelpCenter from '../Help/HelpCenter';
import HelpPage from '../../pages/HelpPage';
import FeedbackPage from '../../pages/FeedbackPage';
import SupportCenter from '../Help/Support/SupportCenter';
import TicketDetailsPage from '../Help/Support/TicketDetailsPage';
import AIStudioIndexDetailPage from '../Settings/AIStudio/AIStudioIndexDetailPage';
import PaymentResults from '../shared/Licenses/PaymentResults';
import LicensesPage from '../shared/Licenses/LicensePage';
import CheckoutPage from '../shared/Licenses/CheckOutPage';
import PlansPage from '../shared/Licenses/PlansPage';
import AIStudioIndexListPage from "../../components/Settings/AIStudio/DataSourcesListPage";
import NotFoundPage from '../shared/TailwindComponents/NotFoundPage';
import DepartmentList from '../Management/Departments/DepartmentList';
import DepartmentDetail from '../Management/Departments/DepartmentDetails';
import FinderBasePage from '../Finder/FinderBasePage';
import NotificationsPage from '../../pages/NotificationsPage';
import FinderCasePage from '../Finder/FinderCasePage';
import LawyerDetailsPage from '../Finder/LawyerDetailsPage';
import Pipeline from '../Pipeline/Pipeline';
import LeadDetailsPage from '../Pipeline/LeadDetailsPage';
import DocumentUploadPage from '../../pages/DocumentUploadPage';
import TemplatesList from '../Templates/TemplatesList';
import SettingsLayout, { SettingsLegacyRedirect } from '../Settings/framework/SettingsLayout';
import { LEGACY_SETTINGS_PATHS } from '../Settings/framework/settingsRegistry';
import InvitationScreen from '../shared/InvitationScreen';
import ExternalPeopleDetail from '../Management/Employees/External/ExternalPeopleDetails';
import TeamsList from '../Management/Teams/TeamList';
import MatterManagementPage from '../../pages/MatterManagementPage';
import MatterDetails from '../Matters/MatterDetails';
import AdminPage from '../../pages/AdminPage';
import CreateRoleForm from '../Admin/CreateRoleForm';
import {
  LEGACY_ADMIN_ROLE_ROUTE,
  LEGACY_ADMIN_USER_ROUTE,
  SETTINGS_DETAIL_ROUTE,
  resolveLegacyAdminDetail,
} from '../Admin/adminRoutes';
import PubSubDebugger from '../Matters/Pusubdebugger';
import BatchTablesPage from '../../pages/BatchTablesPage';
import BatchTableDetail from '../BatchTables/BatchTableDetail';
import StrategyPage from '../../pages/StrategyPage';
import ReviewPage from '../../pages/ReviewPage';
import ReviewDetailPage from '../../pages/ReviewDetailPage';
import ReviewSessionPage from '../../pages/ReviewSessionPage';
import AuditLogPage from '../AuditLog/AuditLogPage';
import DashboardPage from '../../pages/DashboardPage';
import ProjectsPage from '../../pages/ProjectsPage';
import ProjectDetailPage from '../../pages/ProjectDetailPage';



/**
 * Sends a pre-settings admin detail URL to its settings sub-view.
 *
 * Kept as a component rather than a `<Redirect from…>` so the record id
 * survives the hop — an old link to `/admin/users/48` lands on that user, not
 * on the list.
 */
const AdminDetailLegacyRedirect: React.FC = () => {
  const location = useLocation();
  return <Redirect to={resolveLegacyAdminDetail(location.pathname) || '/settings'} />;
};

const PrivateAppRoutes = () => {
  const subscriptionId = useSelector((state: RootState) => state.user.userInfo?.stripe_subscription_id);

  return (
    <Switch>
      <Route path="/pubsub-debug" component={PubSubDebugger} />
      <PrivateRoute
        path="/"
        render={() => (
          <PrivateLayout>
            <Switch>
              <Route exact path="/home" component={DashboardPage} />
              <Route exact path="/dashboard">
                <Redirect to="/home" />
              </Route>
              <LicenseGatedRoute path="/tasks/:taskUuid" exact component={TaskDetail} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/tasks" exact component={TaskDeadlineTracker} subscriptionId={subscriptionId} restricted={false} />
              {/* The notification CENTRE — the bell's sidepanel is the peek,
                  this is the full pile with filters, search and paging. It used
                  to point at `FinderBasePage`, which renders a matter grid. */}
              <PrivateRoute path="/notifications" exact component={NotificationsPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/calendar" exact component={CalendarViewPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/messages" exact component={MessageCenterPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/contacts/:clientId" exact component={ClientDetails} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/contacts" exact component={ClientsList} subscriptionId={subscriptionId} restricted={false} />
              {/* Legacy redirects — /clients → /contacts */}
              <Route exact path="/clients/:clientId">
                {({ match }: any) => <Redirect to={`/contacts/${match.params.clientId}`} />}
              </Route>
              <Route exact path="/clients">
                <Redirect to="/contacts" />
              </Route>
              <LicenseGatedRoute path="/projects/:projectId" exact component={ProjectDetailPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/projects" exact component={ProjectsPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/matters/:matterId" exact component={MatterDetails} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/matters/:matterId/insight/:insightId" exact component={MatterDetails} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/matters/invitation/:matterId/:token" exact component={InvitationScreen} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/matters" exact component={MatterManagementPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/my-time" exact component={MyTimeEntriesPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/economics" exact component={FirmEconomicsDashboardPage} subscriptionId={subscriptionId} restricted={false} />
              {/* AB#1573 — top-level Drafting page (drafts + strategies) */}
              <LicenseGatedRoute path="/drafting/:draftingUuid" exact component={DraftingPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/drafting" exact component={DraftingPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/tables/:tableId" exact component={BatchTableDetail} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/tables" exact component={BatchTablesPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/reviews/:reviewUuid" exact component={ReviewSessionPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/reviews" exact component={ReviewPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/playbooks/:playbookId" exact component={ReviewDetailPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/playbooks" exact component={ReviewPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/files/:folderId?" component={FilesPage} subscriptionId={subscriptionId} restricted={false} />

              {/* /research is the canonical research route. */}
              <LicenseGatedRoute path="/research" exact component={ResearchPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/research/:chatGroupId?" exact component={ResearchPage} subscriptionId={subscriptionId} restricted={false} />
              {/* Back-compat: /research2 was the strangler path during the
                  rebuild. Old bookmarks and links still land on /research. */}
              <Route exact path="/research2/:chatGroupId">
                {({ match }: any) => <Redirect to={`/research/${match.params.chatGroupId}`} />}
              </Route>
              <Route exact path="/research2">
                <Redirect to="/research" />
              </Route>
              <PrivateRoute path="/strategy/:matterId" exact component={StrategyPage} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/strategy" exact component={StrategyPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/pipeline" exact component={Pipeline} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/pipeline/:leadId" exact component={LeadDetailsPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/analytics" exact component={ReportsPage} subscriptionId={subscriptionId} restricted={false} />
              {/* <PrivateRoute path="/audit-log" exact component={AuditLogPage} subscriptionId={subscriptionId} restricted={false} /> */}
              <LicenseGatedRoute path="/data-sources" exact component={AIStudioIndexListPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/data-sources/:dataSourceUuid" exact component={AIStudioIndexDetailPage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/templates" component={TemplatesList} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/people" exact component={EmployeeList} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/people/:employeeId" exact component={EmployeeDetail} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/external/:externalId" component={ExternalPeopleDetail} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/departments" exact component={DepartmentList} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/departments/:departmentId" exact component={DepartmentDetail} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/teams" exact component={TeamsList} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/teams/:teamId" exact component={TeamDetail} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/finance/:tab?" component={FinancePage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/lawyers/:lawyerId" exact component={LawyerDetails} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/lawyers" exact component={Lawyers} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/finder" exact component={FinderBasePage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/finder/:caseId" exact component={FinderCasePage} subscriptionId={subscriptionId} restricted={false} />
              <LicenseGatedRoute path="/finder/:caseId/:lawyerId" exact component={LawyerDetailsPage} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/operations" exact component={OperationPage} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/time-tracking" exact component={TimeTrackingPage} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/time-keeping" exact component={TimeKeepingPage} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/time-keeping/logs" exact component={TimeKeepingLogs} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/help" component={HelpCenter} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/user-manual" exact component={HelpPage} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/feedback" exact component={FeedbackPage} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/help/support" exact component={SupportCenter} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/help/support/:ticketId" exact component={TicketDetailsPage} subscriptionId={subscriptionId} restricted={false} />
              {/* Settings framework — one shell, one registry, one route per section.
                  A record opened from a section (a user, a role) is a SUB-VIEW of
                  that section, so it renders in the same shell: same rail, same
                  header, one breadcrumb. The shell routes on `:sectionId` and the
                  section component reads `:detailId` — no shell change needed to
                  give a section a detail. */}
              <PrivateRoute path={SETTINGS_DETAIL_ROUTE} exact component={SettingsLayout} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/settings/:sectionId" exact component={SettingsLayout} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/settings" exact component={SettingsLayout} subscriptionId={subscriptionId} restricted={false} />
              {/* Legacy settings URLs (incl. their ?tab= deep links) → registry sections. */}
              <Route exact path={LEGACY_SETTINGS_PATHS} component={SettingsLegacyRedirect} />
              {/* `/admin/roles/create` stays a page — it is the create form, not a
                  record, and must be matched before the `:roleId` redirect. */}
              <PrivateRoute path="/admin/roles/create" exact component={CreateRoleForm} subscriptionId={subscriptionId} restricted={false} />
              {/* Old top-level admin detail URLs → their settings sub-views. */}
              <Route exact path={[LEGACY_ADMIN_USER_ROUTE, LEGACY_ADMIN_ROLE_ROUTE]} component={AdminDetailLegacyRedirect} />
              <PrivateRoute path="/admin" exact component={AdminPage} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/payment-result" exact component={PaymentResults} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/plans" exact component={PlansPage} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/plans/checkout" exact component={CheckoutPage} subscriptionId={subscriptionId} restricted={false} />
              <PrivateRoute path="/onboarding/document-upload" component={DocumentUploadPage} subscriptionId={subscriptionId} restricted={false} />

              {/* Redirect root path to /home */}
              <Route exact path="/">
                <Redirect to="/home" />
              </Route>

              {/* 404 fallback */}
              <Route path="*" component={NotFoundPage} />
            </Switch>
          </PrivateLayout>
        )}
        subscriptionId={subscriptionId}
        restricted={false}
      />
    </Switch>
  );
};

export default PrivateAppRoutes;
